import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mutate } from '../shared/api';
import { StoneXAveragePanel } from './stonexAveragePanel';

type Locale = 'hr' | 'en';
type Quote = { Pair: string; Bid: number; Ask: number; MMM?: number };
type Position = { AccountCode: string; Cmdty: string; Position: number };
type Meta = { account: string; env: string; configured: boolean; can_trade?: boolean; max_qty_oz?: number };
type Trade = {
    id: number; cl_ord_id: string; ex_id: string | null; pair: string;
    deal: number; deal_label: 'buy' | 'sell'; quantity_oz: number; rate: number | null; mmm: number | null;
    remarks: string | null; status: string; env: string; is_undo: boolean; undo_of_id: number | null;
    error: string | null; created_by_name: string | null; created_at: string | null;
};
type Fallback = { pair: string; price: number; source: string; as_of: string; fetched_at: string };

const nf = (n: number, d = 2) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const METAL_RE = /^(XAU|XAG|XPT|XPD)/;

export function StoneXModule({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [positions, setPositions] = useState<Position[]>([]);
    const [trades, setTrades] = useState<Trade[]>([]);
    const [meta, setMeta] = useState<Meta | null>(null);
    const [type, setType] = useState<'MTL' | 'ALL' | 'FOR'>('MTL');
    const [goldOnly, setGoldOnly] = useState(true);
    const [error, setError] = useState('');
    const [fetchedAt, setFetchedAt] = useState('');
    const [loading, setLoading] = useState(true);
    const [fallback, setFallback] = useState<Fallback | null>(null);
    const prev = useRef<Record<string, number>>({});

    // Trgovanje (samo superadmin)
    const [fPair, setFPair] = useState('XAUEUR');
    const [fDeal, setFDeal] = useState<1 | 2>(1);
    const [fQty, setFQty] = useState('1');
    const [fRemarks, setFRemarks] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [undoingId, setUndoingId] = useState<number | null>(null);
    const [tradeMsg, setTradeMsg] = useState('');
    const [tradeErr, setTradeErr] = useState('');

    const loadQuotes = useCallback(async () => {
        try {
            const r = await api<{ ok: boolean; rows: Quote[]; error?: string; meta: Meta; fetched_at: string; fallback?: Fallback | null }>(`/stonex/quotes?type=${type}`, token);
            setMeta(r.meta);
            setFallback(r.fallback ?? null);
            if (!r.ok) {
                // StoneX nedostupan (npr. 406 "rates not available"): ako imamo fallback cijenu, ne plaši crvenom greškom
                setError(r.fallback ? '' : (r.error || 'API'));
                setQuotes([]);
                setFetchedAt(r.fetched_at);
                return;
            }
            setError('');
            prev.current = quotes.length ? Object.fromEntries(quotes.map((q) => [q.Pair, (q.Bid + q.Ask) / 2])) : {};
            setQuotes(r.rows);
            setFetchedAt(r.fetched_at);
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
        finally { setLoading(false); }
    }, [token, type, quotes]);

    const loadPositions = useCallback(async () => {
        try { const r = await api<{ ok: boolean; rows: Position[] }>('/stonex/positions', token); setPositions(r.rows ?? []); } catch { /* ignore */ }
    }, [token]);

    const loadTrades = useCallback(async () => {
        try { const r = await api<{ ok: boolean; rows: Trade[] }>('/stonex/trades', token); setTrades(r.rows ?? []); } catch { /* ignore */ }
    }, [token]);

    useEffect(() => { void loadQuotes(); }, [type, token]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { void loadPositions(); }, [loadPositions]);
    useEffect(() => { if (meta?.can_trade) void loadTrades(); }, [meta?.can_trade, loadTrades]);
    // auto-refresh quotes svakih 12s
    useEffect(() => { const id = setInterval(() => { void loadQuotes(); }, 12000); return () => clearInterval(id); }, [loadQuotes]);

    const canTrade = !!meta?.can_trade;
    const maxQty = meta?.max_qty_oz ?? 5000;
    const shown = quotes.filter((q) => !goldOnly || q.Pair.startsWith('XAU'));
    const dir = (q: Quote) => { const mid = (q.Bid + q.Ask) / 2; const p = prev.current[q.Pair]; return p === undefined ? 0 : mid > p ? 1 : mid < p ? -1 : 0; };

    // Trading helpers
    const metalPairs = Array.from(new Set(quotes.map((q) => q.Pair).filter((p) => METAL_RE.test(p))));
    if (!metalPairs.includes(fPair)) metalPairs.unshift(fPair);
    const selQuote = quotes.find((q) => q.Pair === fPair);
    const qtyNum = parseFloat(fQty.replace(',', '.')) || 0;
    const refPrice = selQuote
        ? ((fDeal === 1 ? selQuote.Ask : selQuote.Bid) || (selQuote.Bid + selQuote.Ask) / 2)
        : (fallback && fallback.pair === fPair ? fallback.price : 0);
    const estimate = qtyNum * refPrice;
    const ccy = fPair.length >= 6 ? fPair.slice(3) : '';
    const qtyValid = qtyNum >= 0.01 && qtyNum <= maxQty;
    const resetConfirm = () => { if (confirming) setConfirming(false); };

    const submitTrade = async () => {
        setSubmitting(true); setTradeErr(''); setTradeMsg('');
        try {
            const r = await mutate<{ ok: boolean; error?: string | null; trade?: Trade | null }>('/stonex/trade', token, 'POST', {
                pair: fPair, deal: fDeal, quantity_oz: qtyNum, remarks: fRemarks || undefined,
            });
            if (r.ok) {
                const rate = r.trade?.rate;
                setTradeMsg(t(
                    `✓ Nalog izvršen: ${fDeal === 1 ? 'KUPNJA' : 'PRODAJA'} ${nf(qtyNum, 2)} oz ${fPair}${rate ? ` @ ${nf(rate, 2)} ${ccy}` : ''}`,
                    `✓ Order filled: ${fDeal === 1 ? 'BUY' : 'SELL'} ${nf(qtyNum, 2)} oz ${fPair}${rate ? ` @ ${nf(rate, 2)} ${ccy}` : ''}`,
                ));
                setFRemarks('');
            } else {
                setTradeErr(r.error || t('Nalog odbijen.', 'Order rejected.'));
            }
        } catch (e) { setTradeErr(e instanceof Error ? e.message : 'API'); }
        finally { setSubmitting(false); setConfirming(false); void loadTrades(); void loadPositions(); }
    };

    const doUndo = async (tr: Trade) => {
        const dealTxt = tr.deal_label === 'buy' ? t('KUPNJA', 'BUY') : t('PRODAJA', 'SELL');
        if (!window.confirm(t(
            `Poništiti trade #${tr.id} (${dealTxt} ${nf(tr.quantity_oz, 2)} oz ${tr.pair})?\n\nIzvršit će se SUPROTAN nalog iste količine (offset). Ovo se ne može opozvati.`,
            `Undo trade #${tr.id} (${dealTxt} ${nf(tr.quantity_oz, 2)} oz ${tr.pair})?\n\nAn OPPOSITE order of the same quantity will be executed (offset). This cannot be reversed.`,
        ))) return;
        setUndoingId(tr.id); setTradeErr(''); setTradeMsg('');
        try {
            const r = await mutate<{ ok: boolean; error?: string | null }>(`/stonex/trades/${tr.id}/undo`, token, 'POST', {});
            if (r.ok) setTradeMsg(t(`✓ Trade #${tr.id} poništen (offset izvršen).`, `✓ Trade #${tr.id} undone (offset executed).`));
            else setTradeErr(r.error || t('Poništavanje odbijeno.', 'Undo rejected.'));
        } catch (e) { setTradeErr(e instanceof Error ? e.message : 'API'); }
        finally { setUndoingId(null); void loadTrades(); void loadPositions(); }
    };

    const statusStyle = (s: string): React.CSSProperties => {
        const map: Record<string, [string, string]> = {
            filled: ['#1f7a36', 'rgba(31,122,54,0.12)'],
            failed: ['#b14040', 'rgba(177,64,64,0.12)'],
            undone: ['#6b6b6b', 'rgba(0,0,0,0.08)'],
            inprocess: ['#9a6b12', 'rgba(var(--ad-acc-rgb),0.15)'],
        };
        const [c, bg] = map[s] ?? ['#444', 'rgba(0,0,0,0.06)'];
        return { color: c, background: bg, padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase' };
    };

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📈</div>
                <div className="bi-module-hero-text">
                    <h1>4.1 · StoneX API</h1>
                    <p className="muted">{t('StoneX PMXConnect — live spot cijene plemenitih metala, pozicije i izvršavanje naloga.', 'StoneX PMXConnect — live precious metals spot quotes, positions and order execution.')}</p>
                </div>
            </div>

            {/* Status traka */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fffbeb', borderRadius: 12, padding: '12px 18px', marginBottom: 14 }}>
                <span style={{ fontWeight: 700 }}>StoneX PMXConnect</span>
                {meta ? <span style={{ background: meta.env === 'demo' ? 'rgba(255,255,255,0.2)' : 'rgba(177,64,64,0.6)', padding: '2px 10px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>{meta.env}</span> : null}
                {meta ? <span style={{ opacity: 0.9, fontSize: '0.85rem' }}>{t('Račun', 'Account')}: {meta.account}</span> : null}
                {fetchedAt ? <span style={{ opacity: 0.75, fontSize: '0.78rem' }}>{t('osvježeno', 'updated')}: {new Date(fetchedAt).toLocaleTimeString('hr-HR')}</span> : null}
                <span style={{ marginLeft: 'auto', fontSize: '0.74rem', opacity: 0.85 }}>{canTrade ? `⚡ ${t('trgovanje aktivno', 'trading enabled')}` : `🔒 ${t('read-only', 'read-only')}`}</span>
            </div>

            {error ? <p className="error">{error}{meta && !meta.configured ? ' — ' + t('postavi STONEX_TOKEN u .env', 'set STONEX_TOKEN in .env') : ''}</p> : null}

            {/* Fallback zlatne cijene kad StoneX padne (npr. 406 "rates not available") */}
            {fallback ? (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', background: '#fff7e0', border: '1px solid #e7c66b', color: '#7a5d12', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                    <span style={{ fontWeight: 700 }}>⚠️ {t('StoneX cijene trenutno nedostupne', 'StoneX quotes currently unavailable')}</span>
                    <span>🥇 XAUEUR ≈ <strong>{nf(fallback.price, 2)} €/oz</strong></span>
                    <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>{t('rezervni izvor', 'fallback source')}: {fallback.source}{fallback.as_of ? ` · ${fallback.as_of}` : ''}</span>
                </div>
            ) : null}

            {/* Quotes */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <strong style={{ color: 'var(--ad-ink)' }}>💱 {t('Spot cijene', 'Spot quotes')}</strong>
                <div style={{ display: 'flex', gap: 4 }}>
                    {([['MTL', t('Metali', 'Metals')], ['ALL', t('Sve', 'All')], ['FOR', 'Forex']] as const).map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setType(k)} className={type === k ? '' : 'secondary compact'} style={{ fontSize: '0.78rem' }}>{l}</button>
                    ))}
                </div>
                {type !== 'FOR' ? (
                    <label style={{ fontSize: '0.8rem', display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="checkbox" checked={goldOnly} onChange={(e) => setGoldOnly(e.target.checked)} /> {t('samo zlato (XAU)', 'gold only (XAU)')}
                    </label>
                ) : null}
                <button type="button" className="secondary compact" onClick={() => void loadQuotes()} style={{ marginLeft: 'auto', fontSize: '0.78rem' }}>🔄 {t('Osvježi', 'Refresh')}</button>
            </div>

            {loading && quotes.length === 0 ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, marginBottom: 18 }}>
                    <table className="admin-user-table" style={{ width: '100%', fontSize: '0.86rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                            <tr>
                                <th>{t('Par', 'Pair')}</th>
                                <th style={{ textAlign: 'right' }}>Bid</th>
                                <th style={{ textAlign: 'right' }}>Ask</th>
                                <th style={{ textAlign: 'right' }}>{t('Raspon', 'Spread')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((q) => {
                                const d = dir(q);
                                const isGold = q.Pair.startsWith('XAU');
                                return (
                                    <tr key={q.Pair} style={{ background: isGold ? 'rgba(var(--ad-acc-rgb),0.06)' : undefined }}>
                                        <td style={{ fontWeight: isGold ? 700 : 500, color: isGold ? 'var(--ad-ink)' : undefined }}>
                                            {isGold ? '🥇 ' : ''}{q.Pair}
                                            {d !== 0 ? <span style={{ marginLeft: 6, color: d > 0 ? '#1f7a36' : '#b14040' }}>{d > 0 ? '▲' : '▼'}</span> : null}
                                        </td>
                                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{nf(q.Bid, q.Bid > 100 ? 2 : 5)}</td>
                                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{nf(q.Ask, q.Ask > 100 ? 2 : 5)}</td>
                                        <td style={{ textAlign: 'right', color: 'rgba(0,0,0,0.55)' }}>{nf(q.Ask - q.Bid, (q.Ask - q.Bid) > 1 ? 2 : 4)}</td>
                                    </tr>
                                );
                            })}
                            {shown.length === 0 ? <tr><td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 12 }}>{t('Nema cijena.', 'No quotes.')}</td></tr> : null}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Trgovanje — samo superadmin */}
            {canTrade ? (
                <div style={{ border: '2px solid rgba(var(--ad-acc-rgb),0.55)', borderRadius: 12, padding: '16px 18px', marginBottom: 18, background: 'linear-gradient(180deg, rgba(var(--ad-acc-rgb),0.06), rgba(255,255,255,0))' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '1.02rem' }}>⚡ {t('Izvrši nalog (spot)', 'Place order (spot)')}</strong>
                        <span style={{ ...statusStyle(meta?.env === 'demo' ? 'inprocess' : 'failed'), textTransform: 'uppercase' }}>{meta?.env}</span>
                        {meta?.env === 'demo'
                            ? <span className="muted" style={{ fontSize: '0.78rem' }}>{t('demo račun — nalozi ne diraju pravu poziciju', 'demo account — orders do not touch the real position')}</span>
                            : <span style={{ fontSize: '0.78rem', color: '#b14040', fontWeight: 700 }}>{t('PRAVO TRGOVANJE — nalozi su stvarni!', 'LIVE TRADING — orders are real!')}</span>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 12, alignItems: 'end' }}>
                        <label style={{ fontSize: '0.82rem' }}>
                            <div style={{ marginBottom: 4, color: 'var(--ad-ink)', fontWeight: 600 }}>{t('Par', 'Pair')}</div>
                            <select value={fPair} onChange={(e) => { setFPair(e.target.value); resetConfirm(); }} style={{ width: '100%' }}>
                                {metalPairs.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </label>
                        <div style={{ fontSize: '0.82rem' }}>
                            <div style={{ marginBottom: 4, color: 'var(--ad-ink)', fontWeight: 600 }}>{t('Smjer', 'Side')}</div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" onClick={() => { setFDeal(1); resetConfirm(); }} className={fDeal === 1 ? '' : 'secondary'} style={{ flex: 1, background: fDeal === 1 ? '#1f7a36' : undefined, borderColor: fDeal === 1 ? '#1f7a36' : undefined }}>{t('Kupi', 'Buy')}</button>
                                <button type="button" onClick={() => { setFDeal(2); resetConfirm(); }} className={fDeal === 2 ? '' : 'secondary'} style={{ flex: 1, background: fDeal === 2 ? '#b14040' : undefined, borderColor: fDeal === 2 ? '#b14040' : undefined }}>{t('Prodaj', 'Sell')}</button>
                            </div>
                        </div>
                        <label style={{ fontSize: '0.82rem' }}>
                            <div style={{ marginBottom: 4, color: 'var(--ad-ink)', fontWeight: 600 }}>{t('Količina (oz)', 'Quantity (oz)')}</div>
                            <input type="number" min={0.01} max={maxQty} step={0.01} value={fQty} onChange={(e) => { setFQty(e.target.value); resetConfirm(); }} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: '0.82rem' }}>
                            <div style={{ marginBottom: 4, color: 'var(--ad-ink)', fontWeight: 600 }}>{t('Napomena', 'Remarks')}</div>
                            <input type="text" maxLength={200} value={fRemarks} onChange={(e) => setFRemarks(e.target.value)} placeholder={t('neobavezno', 'optional')} style={{ width: '100%' }} />
                        </label>
                    </div>

                    <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'rgba(0,0,0,0.7)' }}>
                        {selQuote ? <span>{t('Procijenjena vrijednost', 'Estimated value')}: <strong>{nf(estimate, 2)} {ccy}</strong> <span className="muted">({t('po', 'at')} ~{nf(refPrice, 2)} {ccy}/oz)</span></span>
                            : <span className="muted">{t('Nema žive cijene za odabrani par.', 'No live price for selected pair.')}</span>}
                        {!qtyValid ? <span style={{ color: '#b14040', marginLeft: 10 }}>{t(`Količina mora biti 0,01–${nf(maxQty, 0)} oz.`, `Quantity must be 0.01–${nf(maxQty, 0)} oz.`)}</span> : null}
                    </div>

                    {!confirming ? (
                        <button type="button" onClick={() => { setTradeErr(''); setTradeMsg(''); setConfirming(true); }} disabled={!qtyValid || submitting} style={{ marginTop: 12 }}>
                            {t('Pregled naloga →', 'Review order →')}
                        </button>
                    ) : (
                        <div style={{ marginTop: 12, border: '1px solid rgba(var(--ad-acc-rgb),0.6)', borderRadius: 10, padding: '12px 14px', background: 'rgba(var(--ad-acc-rgb),0.08)' }}>
                            <div style={{ fontWeight: 700, color: fDeal === 1 ? '#1f7a36' : '#b14040', marginBottom: 6 }}>
                                {fDeal === 1 ? t('KUPNJA', 'BUY') : t('PRODAJA', 'SELL')} {nf(qtyNum, 2)} oz {fPair}
                                {selQuote ? <span style={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500 }}> ≈ {nf(estimate, 2)} {ccy}</span> : null}
                            </div>
                            <p className="muted" style={{ fontSize: '0.8rem', margin: '0 0 10px' }}>
                                {meta?.env === 'demo'
                                    ? t('Demo nalog — izvršava se na StoneX demo računu.', 'Demo order — executes on the StoneX demo account.')
                                    : t('PRAVO trgovanje — ovaj nalog je stvaran i izvršava se odmah po tržišnoj cijeni.', 'LIVE trading — this is a real order and fills immediately at market.')}
                                {' '}{t('Cijena izvršenja je tržišna u trenutku slanja.', 'Fill price is the market price at submit time.')}
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button" onClick={() => void submitTrade()} disabled={submitting} style={{ background: fDeal === 1 ? '#1f7a36' : '#b14040', borderColor: fDeal === 1 ? '#1f7a36' : '#b14040' }}>
                                    {submitting ? t('Šaljem…', 'Sending…') : t('✓ Potvrdi i pošalji', '✓ Confirm & send')}
                                </button>
                                <button type="button" className="secondary" onClick={() => setConfirming(false)} disabled={submitting}>{t('Odustani', 'Cancel')}</button>
                            </div>
                        </div>
                    )}

                    {tradeMsg ? <p style={{ color: '#1f7a36', fontWeight: 600, marginTop: 10, marginBottom: 0 }}>{tradeMsg}</p> : null}
                    {tradeErr ? <p className="error" style={{ marginTop: 10, marginBottom: 0 }}>{tradeErr}</p> : null}
                </div>
            ) : null}

            {/* Pozicije */}
            <strong style={{ color: 'var(--ad-ink)' }}>📊 {t('Pozicije računa', 'Account positions')}</strong>
            <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, marginTop: 8, marginBottom: canTrade ? 18 : 0 }}>
                <table className="admin-user-table" style={{ width: '100%', fontSize: '0.86rem' }}>
                    <thead><tr><th>{t('Račun', 'Account')}</th><th>{t('Roba/valuta', 'Commodity/Currency')}</th><th style={{ textAlign: 'right' }}>{t('Pozicija', 'Position')}</th></tr></thead>
                    <tbody>
                        {positions.map((p, i) => (
                            <tr key={i}><td>{p.AccountCode}</td><td style={{ fontWeight: 600 }}>{p.Cmdty}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{nf(p.Position, 2)}</td></tr>
                        ))}
                        {positions.length === 0 ? <tr><td colSpan={3} className="muted" style={{ textAlign: 'center', padding: 12 }}>{t('Nema otvorenih pozicija.', 'No open positions.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {/* Dnevnik naloga — samo superadmin */}
            {canTrade ? (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <strong style={{ color: 'var(--ad-ink)' }}>🧾 {t('Dnevnik naloga', 'Order log')}</strong>
                        <button type="button" className="secondary compact" onClick={() => void loadTrades()} style={{ marginLeft: 'auto', fontSize: '0.78rem' }}>🔄 {t('Osvježi', 'Refresh')}</button>
                    </div>
                    <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8 }}>
                        <table className="admin-user-table" style={{ width: '100%', fontSize: '0.84rem' }}>
                            <thead>
                                <tr>
                                    <th>{t('Vrijeme', 'Time')}</th>
                                    <th>{t('Par', 'Pair')}</th>
                                    <th>{t('Smjer', 'Side')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Količina', 'Qty')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Cijena', 'Rate')}</th>
                                    <th>{t('Status', 'Status')}</th>
                                    <th>{t('Napomena', 'Remarks')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {trades.map((tr) => {
                                    const canUndo = tr.status === 'filled' && !tr.is_undo;
                                    return (
                                        <tr key={tr.id} style={{ opacity: tr.status === 'undone' ? 0.6 : 1 }}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{tr.created_at ? new Date(tr.created_at).toLocaleString('hr-HR') : '—'}</td>
                                            <td style={{ fontWeight: 600 }}>{tr.pair}</td>
                                            <td>
                                                <span style={{ color: tr.deal_label === 'buy' ? '#1f7a36' : '#b14040', fontWeight: 700 }}>
                                                    {tr.is_undo ? '↩ ' : ''}{tr.deal_label === 'buy' ? t('Kupnja', 'Buy') : t('Prodaja', 'Sell')}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{nf(tr.quantity_oz, 2)}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{tr.rate !== null ? nf(tr.rate, 2) : '—'}</td>
                                            <td><span style={statusStyle(tr.status)}>{tr.status}</span></td>
                                            <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tr.error || tr.remarks || ''}>
                                                {tr.error ? <span style={{ color: '#b14040' }}>{tr.error}</span> : (tr.remarks || '—')}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {canUndo ? (
                                                    <button type="button" className="secondary compact" disabled={undoingId === tr.id} onClick={() => void doUndo(tr)} style={{ fontSize: '0.74rem' }}>
                                                        {undoingId === tr.id ? '…' : `↩ ${t('Poništi', 'Undo')}`}
                                                    </button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {trades.length === 0 ? <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 12 }}>{t('Još nema naloga.', 'No orders yet.')}</td></tr> : null}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}

            <StoneXAveragePanel token={token} locale={locale} />

            <p className="muted" style={{ fontSize: '0.76rem', marginTop: 12, fontStyle: 'italic' }}>
                {canTrade
                    ? t('Trgovanje je dostupno samo superadministratoru. "Poništi" izvršava suprotan nalog iste količine (offset/flatten) — ne briše izvorni trade. Prebacivanje na pravo trgovanje ide promjenom STONEX_BASE_URL / STONEX_TOKEN / STONEX_ENV_LABEL u .env.',
                        'Trading is available to the superadministrator only. "Undo" executes an opposite order of the same quantity (offset/flatten) — it does not delete the original trade. Switching to live trading is done by changing STONEX_BASE_URL / STONEX_TOKEN / STONEX_ENV_LABEL in .env.')
                    : t('Izvršavanje naloga je omogućeno samo superadministratoru.', 'Order execution is enabled for the superadministrator only.')}
            </p>
        </section>
    );
}
